import { toast } from 'sonner'

import { useCallback, useEffect, useState } from 'react'

import { stringifyGraph } from '@/clients/http'
import { useAuth } from '@/contexts/AuthContext'
import { CustomerModel } from '@/http/models/customer/Customer'
import { type PriceBook } from '@/http/models/PosCatalog'
import { CustomerModel as CustomerType } from '@/types'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'

const LENGTH = 50
const GRAPH = stringifyGraph({ _id: true, name: true, phone: true, price_book_id: true })

interface Props {
  book: PriceBook | null
  books: PriceBook[]
  onClose: () => void
}

/** The customers on a price book: they pay the book's prices at the till. Add or take them off here. */
export const BookCustomersDialog = ({ book, books, onClose }: Props) => {
  const { can } = useAuth()
  const editable = can('customers:update')
  const [members, setMembers] = useState<CustomerType.Customer[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search), 300)
    return () => window.clearTimeout(t)
  }, [search])
  const [found, setFound] = useState<CustomerType.Customer[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!book) return
    setLoading(true)
    const res = await CustomerModel.getList({ query: { price_book_id: book._id, length: LENGTH, graph_enc: GRAPH } })
    if (res.success && res.data) {
      setMembers(res.data.result)
      setCount(res.data.count)
    }
    setLoading(false)
  }, [book])

  useEffect(() => {
    setMembers([])
    setSearch('')
    setFound([])
    load()
  }, [load])

  useEffect(() => {
    if (!book || debounced.trim().length < 2) return setFound([])
    CustomerModel.getList({ query: { search: debounced.trim(), length: 10, graph_enc: GRAPH } }).then((res) => {
      if (res.success && res.data) setFound(res.data.result)
    })
  }, [book, debounced])

  const setBook = async (customer: CustomerType.Customer, priceBookId: string | null) => {
    setBusy(customer._id)
    const res = await CustomerModel.update({ params: { id: customer._id }, body: { price_book_id: priceBookId } })
    setBusy(null)
    if (!res.success) return toast.error('Could not change the customer’s price book')
    toast.success(priceBookId ? `${customer.name} is on ${book?.title}` : `${customer.name} is back on normal prices`)
    setFound((list) => list.map((c) => (c._id === customer._id ? { ...c, price_book_id: priceBookId } : c)))
    load()
  }

  const bookName = (id?: string | null) => books.find((b) => b._id === id)?.title ?? 'another book'

  return (
    <Dialog open={!!book} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Customers on {book?.title}</DialogTitle>
          <DialogDescription>
            {count} {count === 1 ? 'customer pays' : 'customers pay'} this book's prices at the till, at every branch where the book has a price.
          </DialogDescription>
        </DialogHeader>

        {editable && (
          <div className="space-y-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Add a customer: search by name or phone" />
            {found.length > 0 && (
              <ul className="divide-y divide-border-subtle rounded-md border border-border">
                {found.map((c) => {
                  const here = c.price_book_id === book?._id
                  return (
                    <li key={c._id} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium text-ink">{c.name}</span> <span className="text-muted-foreground">{c.phone}</span>
                        {c.price_book_id && !here && <span className="ml-2 text-xs text-muted-foreground">now on {bookName(c.price_book_id)}</span>}
                      </span>
                      <Button size="sm" variant="outline" disabled={here || busy === c._id} onClick={() => book && setBook(c, book._id)}>
                        {here ? 'Added' : c.price_book_id ? 'Move here' : 'Add'}
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : members.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No customers on this book yet.</p>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {members.map((c) => (
                <li key={c._id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium text-ink">{c.name}</span> <span className="text-muted-foreground">{c.phone}</span>
                  </span>
                  {editable && (
                    <Button size="sm" variant="ghost" disabled={busy === c._id} onClick={() => setBook(c, null)}>
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {count > members.length && <p className="pt-2 text-xs text-muted-foreground">Showing the first {members.length} of {count}.</p>}
        </div>
      </DialogContent>
    </Dialog>
  )
}
